import { Box, Link } from '@mui/material';
import { useCallback, useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
  SIDEKICK_FEATURE_EVENT,
  useSidekickFeatureEventReporter,
} from '../../featureEvents';
import { useSidekickConsentGate } from '../../hooks/useSidekickConsentGate';
import { CONSENT_STATUS } from '../../state/aiSidekickConsentSlice';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import InlineMessageCard from '../InlineMessageCard';
import { showSidekickErrorToast } from '../toast';
import useStyles from './SidekickConsentGate.styles';

/**
 * First-time / not-yet-consented gate rendered inside the Sidekick
 * drawer. A 60%-opacity white backdrop dims (but keeps visible) the chat
 * behind it and blocks interaction until the user resolves the legal
 * disclaimer, with the consent card centered on top (Figma "Version B:
 * In-drawer modal", frame `17525:31415`).
 *
 * Exposed as a labelled modal dialog (`role="dialog"` + `aria-modal` +
 * `aria-label`) and moves focus into itself on mount so keyboard and
 * screen-reader users land on the gate.
 *
 * The card is the shared {@link InlineMessageCard} primitive:
 * - title → "Using Fleet AI Assistant"
 * - body → consent copy with an embedded privacy-statement link
 *   (rendered via `<Trans>` so the anchor is interactive)
 * - secondary action → Disagree (declines → collapses the drawer)
 * - primary action → Agree (records consent; loading while submitting)
 * - dismiss glyph → same as Disagree (no consent recorded)
 *
 * All behavior is delegated to {@link useSidekickConsentGate}; this
 * component owns only presentation. It renders unconditionally when
 * mounted — the parent (`AISidekickPanel`) mounts it whenever the
 * panel is open and consent is not yet granted (fail closed).
 */
function SidekickConsentGate() {
  const { classes } = useStyles();
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const { status, agree, decline } = useSidekickConsentGate();
  const { captureEmit } = useSidekickFeatureEventReporter();
  const isSubmitting = status === CONSENT_STATUS.SUBMITTING;
  const overlayRef = useRef<HTMLDivElement>(null);

  // Move focus into the dialog on mount so keyboard + screen-reader
  // users land on the consent gate (mirrors ResetConversationPrompt).
  useEffect(() => {
    overlayRef.current?.focus();
  }, []);

  const handleAgree = useCallback(() => {
    const emitAccepted = captureEmit();
    // Presentation of the failure toast lives in the component (not the
    // hook), matching the package convention.
    void agree().then((ok) => {
      if (ok) {
        emitAccepted({ type: SIDEKICK_FEATURE_EVENT.CONSENT_ACCEPTED });
        return;
      }
      showSidekickErrorToast({
        message: t('chat.error.network.toast'),
        closeAriaLabel: t('chat.error.network.toastDismissAriaLabel'),
        testId: 'ai-sidekick-consent-error-toast',
      });
    });
  }, [agree, captureEmit, t]);
  const hrefLink = t('consent.privacyLinkHref');
  const ariaLabel = t('consent.title');

  return (
    <Box
      ref={overlayRef}
      className={classes.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      tabIndex={-1}
      data-testid="ai-sidekick-consent-gate"
    >
      <InlineMessageCard
        className={classes.card}
        data-testid="ai-sidekick-consent-card"
        title={t('consent.title')}
        body={
          <Trans
            t={t}
            i18nKey="consent.body"
            components={{
              privacyLink: (
                <Link
                  className={classes.link}
                  href={hrefLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="ai-sidekick-consent-privacy-link"
                />
              ),
            }}
          />
        }
        onDismiss={decline}
        closeAriaLabel={t('consent.closeAriaLabel')}
        secondaryAction={{
          label: t('consent.disagreeLabel'),
          onClick: decline,
          'data-testid': 'ai-sidekick-consent-disagree',
        }}
        primaryAction={{
          label: t('consent.agreeLabel'),
          onClick: handleAgree,
          isLoading: isSubmitting,
          loadingAriaLabel: t('consent.agreeLoadingAriaLabel'),
          'data-testid': 'ai-sidekick-consent-agree',
        }}
      />
    </Box>
  );
}

export default SidekickConsentGate;
